/**
 * BLRRD Tools — Advanced Eyedropper (Phase 4.E.tools.1, Wave 12.1).
 *
 * Samples a pixel (or rectangular average) from a Float32 ACEScg-linear
 * scene-referred RGBA buffer and returns all the colour-space readouts
 * a colourist actually wants:
 *
 *   • ACEScg linear      — the working-space value (no transforms applied)
 *   • sRGB linear        — convenience for tools that want linear-light Rec.709
 *   • sRGB display       — post-encode display value [0..1]
 *   • sRGB display 8-bit — the same value quantised to 0..255 ints
 *   • CIE L*a*b*         — D65 illuminant, perceptual ΔE-friendly
 *   • CIE LCh            — L*a*b* in cylindrical (L, chroma, hue°) form
 *
 * Coordinates: (x, y) are integer pixel coordinates. radius=0 samples a
 * single pixel; radius=r averages a (2r+1)×(2r+1) box clipped to the
 * buffer extent. Out-of-range coordinates clip; we never throw on bad
 * coords — the UI must remain interactive even when the cursor strays
 * off the canvas mid-drag.
 *
 * ColorHistory: FIFO ring buffer. Push 10 samples into capacity-8 →
 * the oldest two are evicted. `get()` returns a fresh array of the
 * most-recent-first ordering so the consumer can render thumbnails
 * left-to-right without re-reversing.
 *
 * License: MIT clean-room. Lab/LCh math from Hughes, Computer Graphics:
 * Principles and Practice (3rd ed.) §28.4 and CIE Pub. 15. No GIMP
 * source consulted.
 */

import { M_ACEScg_to_sRGB, encodeSRGB } from '../color/spaces.js';

/* ── CIE Lab / LCh (D65 illuminant) ──────────────────────────────────── */

/* CIE D65 reference whitepoint, normalised so Y = 1. Standard values from
 * CIE 15:2018 — used everywhere from Photoshop's CIELAB to web canvas
 * colour. Hughes §28.4 derives the conversion below. */
const D65_X = 0.95047;
const D65_Y = 1.00000;
const D65_Z = 1.08883;

/* sRGB-linear → CIE XYZ (D65). Bradford-adapted; values match the
 * "Bruce Lindbloom" reference rounded to 4 sf, also identical to the
 * matrix in WebKit's filter pipeline. We keep them inline rather than
 * importing from spaces.js because spaces.js exposes sRGB-via-ACEScg
 * (which is the working-space chain, not what we want for Lab). */
const M_LIN_SRGB_TO_XYZ = [
    0.4124564, 0.3575761, 0.1804375,
    0.2126729, 0.7151522, 0.0721750,
    0.0193339, 0.1191920, 0.9503041,
];

function f_lab(t) {
    /* Forward "f" from CIE Lab. δ = 6/29; below the cube of δ we use the
     * linear approximation, above we use the cube-root branch. */
    const delta = 6 / 29;
    const delta3 = delta * delta * delta;
    return t > delta3
        ? Math.cbrt(t)
        : (t / (3 * delta * delta)) + (4 / 29);
}

function f_lab_inv(t) {
    const delta = 6 / 29;
    return t > delta
        ? t * t * t
        : 3 * delta * delta * (t - 4 / 29);
}

/* Linear sRGB triplet → CIE XYZ (D65). */
function linRGBtoXYZ(r, g, b) {
    const m = M_LIN_SRGB_TO_XYZ;
    return [
        m[0] * r + m[1] * g + m[2] * b,
        m[3] * r + m[4] * g + m[5] * b,
        m[6] * r + m[7] * g + m[8] * b,
    ];
}

/* CIE XYZ → Lab (D65). */
function xyzToLab(X, Y, Z) {
    const fx = f_lab(X / D65_X);
    const fy = f_lab(Y / D65_Y);
    const fz = f_lab(Z / D65_Z);
    return [
        116 * fy - 16,         /* L*  ∈ [0, 100] */
        500 * (fx - fy),       /* a*  green↔red */
        200 * (fy - fz),       /* b*  blue↔yellow */
    ];
}

/* Lab → XYZ — only used by the round-trip test gate. */
export function labToXYZ(L, a, b) {
    const fy = (L + 16) / 116;
    const fx = fy + a / 500;
    const fz = fy - b / 200;
    return [
        D65_X * f_lab_inv(fx),
        D65_Y * f_lab_inv(fy),
        D65_Z * f_lab_inv(fz),
    ];
}

/* Linear sRGB → Lab (D65). Public so callers can compute ΔE between
 * arbitrary swatches without going through a full samplePixel. */
export function linRGBtoLab(r, g, b) {
    const [X, Y, Z] = linRGBtoXYZ(r, g, b);
    return xyzToLab(X, Y, Z);
}

/* Lab → LCh (cylindrical). h in degrees [0, 360). */
export function labToLCh(L, a, b) {
    const c = Math.sqrt(a * a + b * b);
    let h = Math.atan2(b, a) * (180 / Math.PI);
    if (h < 0) h += 360;
    return [L, c, h];
}

/* ── ACEScg → sRGB-linear (no encode) ──────────────────────────────── */

function acescgToLinSRGB(r, g, b) {
    const m = M_ACEScg_to_sRGB;
    return [
        m[0] * r + m[1] * g + m[2] * b,
        m[3] * r + m[4] * g + m[5] * b,
        m[6] * r + m[7] * g + m[8] * b,
    ];
}

/* ── samplePixel ───────────────────────────────────────────────────── */

/**
 * Sample a pixel (or local average) from a Float32 ACEScg RGBA buffer.
 *
 * @param {Float32Array} rgba   length = w*h*4, scene-linear ACEScg
 * @param {number}       w      buffer width
 * @param {number}       h      buffer height
 * @param {number}       x      pixel x (integer, clipped to [0, w-1])
 * @param {number}       y      pixel y (integer, clipped to [0, h-1])
 * @param {number}       [radius=0]   averaging radius — 0 = single pixel
 *                                    1 = 3×3 box, 2 = 5×5, ...
 * @returns {{
 *   aces: [number,number,number,number],
 *   sRGBLinear: [number,number,number,number],
 *   sRGB: [number,number,number,number],
 *   sRGB8: [number,number,number,number],
 *   lab: [number,number,number],
 *   lch: [number,number,number],
 *   pos: { x: number, y: number },
 *   radius: number,
 *   sampleCount: number,
 * }}
 */
export function samplePixel(rgba, w, h, x, y, radius = 0) {
    if (!rgba || !Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
        throw new Error('samplePixel: invalid buffer dimensions');
    }
    if (rgba.length < w * h * 4) {
        throw new Error('samplePixel: buffer too short for w*h*4');
    }

    /* Clip x, y. We don't throw — the cursor often skirts the canvas
     * edge during a hover and the readout must still update. */
    const cx = Math.max(0, Math.min(w - 1, Math.round(x)));
    const cy = Math.max(0, Math.min(h - 1, Math.round(y)));
    const r  = Math.max(0, Math.floor(radius));

    const x0 = Math.max(0, cx - r);
    const x1 = Math.min(w - 1, cx + r);
    const y0 = Math.max(0, cy - r);
    const y1 = Math.min(h - 1, cy + r);

    let sumR = 0, sumG = 0, sumB = 0, sumA = 0;
    let count = 0;
    for (let yy = y0; yy <= y1; yy++) {
        const row = yy * w * 4;
        for (let xx = x0; xx <= x1; xx++) {
            const j = row + xx * 4;
            sumR += rgba[j];
            sumG += rgba[j + 1];
            sumB += rgba[j + 2];
            sumA += rgba[j + 3];
            count++;
        }
    }
    if (count === 0) count = 1; /* defensive — should not happen */
    const aR = sumR / count;
    const aG = sumG / count;
    const aB = sumB / count;
    const aA = sumA / count;

    /* ACEScg → sRGB-linear via the pre-baked 3×3 matrix. */
    const [lR, lG, lB] = acescgToLinSRGB(aR, aG, aB);

    /* sRGB display = encode(clamp(lin)). Encode is non-linear so we
     * must clamp BEFORE encode to avoid NaN on negative ACEScg values
     * (which legally occur in ACEScg gamut). */
    const cR = lR < 0 ? 0 : lR > 1 ? 1 : lR;
    const cG = lG < 0 ? 0 : lG > 1 ? 1 : lG;
    const cB = lB < 0 ? 0 : lB > 1 ? 1 : lB;
    const dR = encodeSRGB(cR);
    const dG = encodeSRGB(cG);
    const dB = encodeSRGB(cB);

    /* 8-bit display values — rounded to nearest. */
    const i8R = Math.max(0, Math.min(255, Math.round(dR * 255)));
    const i8G = Math.max(0, Math.min(255, Math.round(dG * 255)));
    const i8B = Math.max(0, Math.min(255, Math.round(dB * 255)));
    const i8A = Math.max(0, Math.min(255, Math.round(aA * 255)));

    /* Lab on linear sRGB (clamped — Lab is undefined outside the visible
     * spectrum, and the picker UX wants stable readouts). LCh follows. */
    const lab = linRGBtoLab(cR, cG, cB);
    const lch = labToLCh(lab[0], lab[1], lab[2]);

    return {
        aces:       [aR, aG, aB, aA],
        sRGBLinear: [lR, lG, lB, aA],
        sRGB:       [dR, dG, dB, aA],
        sRGB8:      [i8R, i8G, i8B, i8A],
        lab,
        lch,
        pos: { x: cx, y: cy },
        radius: r,
        sampleCount: count,
    };
}

/* ── Color history (FIFO ring) ─────────────────────────────────────── */

/**
 * Capacity-bounded FIFO ring of colour samples. Oldest entries are evicted
 * when capacity is exceeded.
 *
 * @param {number} [capacity=8]
 */
export function createColorHistory(capacity = 8) {
    const cap = Math.max(1, Math.floor(capacity));
    const buf = [];

    function push(sample) {
        if (!sample) return;
        /* Defensive copy of the colour triplets — caller may mutate
         * the returned `aces` array on the result of samplePixel
         * (e.g. clamping for UI display). */
        const copy = {
            aces:       (sample.aces       || []).slice(),
            sRGBLinear: (sample.sRGBLinear || []).slice(),
            sRGB:       (sample.sRGB       || []).slice(),
            sRGB8:      (sample.sRGB8      || []).slice(),
            lab:        (sample.lab        || []).slice(),
            lch:        (sample.lch        || []).slice(),
            pos:        sample.pos ? { x: sample.pos.x, y: sample.pos.y } : null,
            radius:     sample.radius || 0,
            ts:         Date.now(),
        };
        buf.push(copy);
        while (buf.length > cap) buf.shift();
    }

    function get() {
        /* Most-recent-first ordering so UI consumers can render
         * left-to-right without re-reversing. */
        return buf.slice().reverse();
    }

    function clear() { buf.length = 0; }

    return { push, get, clear, get capacity() { return cap; }, get size() { return buf.length; } };
}

/* ── Foreground colour helpers ─────────────────────────────────────── */

/**
 * Build a fg-colour record from a sample. The bucket + gradient tools
 * consume `aces` directly; the colour picker UI consumes `sRGB8` for
 * the swatch chip.
 */
export function sampleToForeground(sample) {
    if (!sample) return null;
    return {
        aces:  sample.aces.slice(),
        sRGB8: sample.sRGB8.slice(),
    };
}
